import type { UserHomeActivityCardData } from '../user-home/components/ActivityCard';
import type { AgendaItem, AgendaOnlineItem } from './types';

type Translate = (key: string, params?: Record<string, string | number | boolean | null | undefined>, fallback?: string) => string;

/**
 * Transforme un item du read-model agenda en données de carte
 * (UserHomeActivityCardData), en reproduisant la logique de badges/détails
 * de GasCardRenderer::createActivityCardData côté PHP.
 */
export function agendaItemToCard(item: AgendaItem, t: Translate): UserHomeActivityCardData {
    const imageBadges = [{ text: item.type_label, type: 'default' }];
    if (item.is_free) {
        imageBadges.unshift({ text: t('agenda.badge.free', {}, 'Gratuit'), type: 'success' });
    }
    if (item.parent) {
        imageBadges.push({ text: item.parent.title, type: 'info' });
    }

    const metaBadges: Array<{ text: string; type: string }> = [];
    for (const badge of item.membership_coverage?.badges ?? []) {
        metaBadges.push({
            text: badge.key ? t(`agenda.badge.${badge.key}`, {}, badge.text) : badge.text,
            type: badge.type || 'warning',
        });
    }
    if (item.prerequis_parent) {
        metaBadges.push({ text: t('agenda.badge.prerequis_parent', {}, 'Activité de base requise'), type: 'danger' });
    }
    // Badge compact uniquement (ex. « 3 sessions ») — jamais la liste des dates
    // (white-space:nowrap sur .gas-card__badge--meta → débordement).
    if (item.moments_label) {
        metaBadges.push({ text: item.moments_label, type: 'info' });
    }

    // Ligne date : première + dernière session si multi-moments, sinon date parent.
    const sessionCount = item.sessions?.length ?? 0;
    let dateText = item.date_label;
    if (sessionCount > 1 && item.sessions) {
        const first = item.sessions[0]?.date_short || item.sessions[0]?.date_label || '';
        const last = item.sessions[sessionCount - 1]?.date_short
            || item.sessions[sessionCount - 1]?.date_label
            || '';
        if (first && last && first !== last) {
            dateText = `${first} → ${last}`;
        } else if (first) {
            dateText = first;
        }
    }

    const details: Array<{ icon?: string; text?: string; html?: boolean }> = [
        { icon: '📅', text: dateText },
    ];
    if (item.duration_label && !(item.sessions && item.sessions.length > 1)) {
        details.push({ icon: '⏱️', text: item.duration_label });
    }
    if (item.animators_label) {
        details.push({ icon: '👤', text: item.animators_label });
    }
    // Prix d'appel (min variantes). Gratuit = gratuit pour tous ;
    // sinon « Dès X CHF » (X peut être 0 via pass/membre — le badge pass
    // complète le message).
    let priceHtml: string;
    if (item.is_free) {
        priceHtml = `<strong>${t('agenda.price.free', {}, 'Gratuit')}</strong>`;
    } else {
        const amount = Number(item.price_from);
        const amountLabel = Number.isFinite(amount)
            ? (amount === 0 || amount === Math.floor(amount)
                ? String(amount)
                : amount.toFixed(2))
            : String(item.price_from ?? '');
        priceHtml = t('agenda.price.from', { amount: amountLabel }, `Dès ${amountLabel} CHF`);
    }
    details.push({ icon: '💰', text: priceHtml, html: true });

    return {
        id: item.id,
        title: item.title,
        url: item.url,
        image_url: item.image_url,
        description: item.intro,
        details,
        meta_badges: metaBadges,
        image_badges: imageBadges,
        actions: [],
    };
}

export function agendaOnlineItemToCard(item: AgendaOnlineItem, t: Translate): UserHomeActivityCardData {
    const card = agendaItemToCard(item, t);
    if (item.videos_duration_label) {
        card.details = [
            ...(card.details ?? []),
            { icon: '🎬', text: item.videos_duration_label },
        ];
    }
    return card;
}

/**
 * Card allégée pour le bandeau « Replays disponibles » : image + type + titre
 * (+ intro courte). Pas de date, prix, pass, durée — pour rester compact.
 */
export function agendaOnlineTeaserToCard(item: AgendaOnlineItem, t: Translate): UserHomeActivityCardData {
    return {
        id: item.id,
        title: item.title,
        url: item.url,
        image_url: item.image_url,
        description: item.intro || '',
        details: [],
        meta_badges: [],
        image_badges: item.type_label
            ? [{ text: item.type_label, type: 'default' }]
            : [],
        actions: [],
    };
}
